import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePaymentService } from '../services/payment.service';
import { usePricingService } from '../services/pricing.service';

/**
 * Hook for using payment services with React Query
 */
export const usePaymentQueries = (countryCode: string = 'DE') => {
    const pricingService = usePricingService();
    const paymentService = usePaymentService();
    const queryClient = useQueryClient();

    // Get pricing data
    const pricingQuery = useQuery({
        queryKey: ['pricing', countryCode],
        queryFn: () => pricingService.getPricingData(countryCode),
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    // Cello UCC validation query
    const useCelloUccValidation = (ucc: string | null) => {
        return useQuery({
            queryKey: ['celloUccValidation', ucc],
            queryFn: () => paymentService.validateCelloUcc(ucc!), // The non-null assertion is safe due to 'enabled'
            enabled: !!ucc, // Only run if ucc is provided
            staleTime: 1000 * 60 * 60, // 1 hour
            retry: false, // Optional: prevent retries if UCC is invalid on first attempt
        });
    };

    // Create checkout session mutation
    const createCheckoutSessionMutation = useMutation({
        mutationFn: paymentService.createCheckoutSession,
        onSuccess: (data) => {
            console.log('[Checkout] onSuccess - data received:', data);
            queryClient.invalidateQueries({ queryKey: ['userPlans'] });
            if (data && data.url) {
                window.location.href = data.url;
            } else {
                console.error(
                    '[Checkout] Error creating checkout session: No redirect URL received (or data missing)',
                    data
                );
            }
        },
        onError: (error) => {
            console.error('[Checkout] Error creating checkout session:', error);
        },
    });

    // Get user plans query
    const userPlansQuery = useQuery({
        queryKey: ['userPlans'],
        queryFn: paymentService.getUserPlans,
    });

    /**
     * Hook to create a refund
     */
    const useCreateRefund = () => {
        return useMutation({
            mutationFn: ({ paymentIntentId, reason }: { paymentIntentId: string; reason?: string }) =>
                paymentService.createRefund(paymentIntentId, reason),
        });
    };

    /**
     * Hook to get refund status
     * @param refundId The refund ID to check
     */
    const useRefundStatus = (refundId: string | undefined) => {
        return useQuery({
            queryKey: ['refundStatus', refundId],
            queryFn: () => paymentService.getRefundStatus(refundId!),
            enabled: !!refundId,
        });
    };

    /**
     * Hook to get invoice by payment ID
     */
    const useGetInvoice = () => {
        return useMutation({
            mutationFn: paymentService.getInvoice,
        });
    };

    /**
     * Hook to get all invoices for the current user
     */
    const useGetAllInvoices = () => {
        return useQuery({
            queryKey: ['invoices'],
            queryFn: paymentService.getAllInvoices,
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
        });
    };

    // Fetch invoices if user has plans
    const invoicesQuery = useQuery({
        queryKey: ['invoices'],
        queryFn: paymentService.getAllInvoices,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
        enabled: !!userPlansQuery.data, // Only fetch if user has plans
    });

    /**
     * Hook to get individual payment pricing for custom payment links
     * @param enabled Whether to enable the query (e.g., when individual-payment-link param is present)
     */
    const useIndividualPaymentPricing = (enabled: boolean = false) => {
        return useQuery({
            queryKey: ['individual-payment-pricing'],
            queryFn: () => paymentService.getIndividualPaymentPricing(),
            enabled: enabled,
            retry: false,
            gcTime: 0, // Don't cache the data
            staleTime: 0, // Always consider data stale
        });
    };

    return {
        createCheckoutSessionMutation,
        createCheckoutSession: createCheckoutSessionMutation.mutateAsync,
        isLoading: createCheckoutSessionMutation.isPending,
        error: createCheckoutSessionMutation.error,
        userPlans: userPlansQuery.data,
        isLoadingUserPlans: userPlansQuery.isLoading,
        userPlansError: userPlansQuery.error,
        pricing: pricingQuery.data,
        createRefund: paymentService.createRefund,
        getRefundStatus: paymentService.getRefundStatus,
        useCreateRefund,
        useRefundStatus,
        createBillingPortalSession: paymentService.createBillingPortalSession,
        useCelloUccValidation,
        validateCelloUcc: paymentService.validateCelloUcc,
        getInvoice: paymentService.getInvoice,
        useGetInvoice,
        getAllInvoices: paymentService.getAllInvoices,
        useGetAllInvoices,
        invoices: invoicesQuery.data?.invoices || [],
        isLoadingInvoices: invoicesQuery.isLoading,
        invoicesError: invoicesQuery.error,
        useIndividualPaymentPricing,
    };
};
